import { Link, useLocation } from 'react-router-dom';
import { Home, LayoutGrid, ShoppingCart, User } from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ROLES, homePathFor } from '@/features/auth/roles';
import { useCartCount } from '@/features/cart/hooks/useCart';
import { cn } from '@/ui/cn';

/**
 * The shop's tab bar on a phone, built like a shopping app's: four places,
 * the active one with a pill behind its icon (the same look as the console's
 * tab bar), and the basket count on the Cart tab.
 *
 * 64px tall plus the safe area - StickyActionBar's `aboveNav` sits on top of
 * exactly that, so keep the two in step.
 */
export function MobileBottomNav() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const itemCount = useCartCount();

  // A customer's account lives at /account; staff go to their own console.
  const accountHref = !user
    ? '/login'
    : user.role === ROLES.CUSTOMER
      ? '/account'
      : homePathFor(user.role);

  const tabs = [
    { to: '/', label: 'Home', icon: Home, active: pathname === '/' },
    { to: '/products', label: 'Shop', icon: LayoutGrid, active: pathname.startsWith('/products') },
    {
      to: '/cart',
      label: 'Cart',
      icon: ShoppingCart,
      badge: itemCount,
      active: pathname === '/cart' || pathname === '/checkout',
    },
    {
      to: accountHref,
      label: 'Account',
      icon: User,
      active: pathname.startsWith('/account') || pathname === '/login',
    },
  ];

  return (
    <nav
      aria-label="Shop"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md md:hidden"
    >
      <div className="flex h-16 items-stretch">
        {tabs.map(({ to, label, icon: Icon, badge, active }) => (
          <Link
            key={label}
            to={to}
            // Worked out above rather than by NavLink: Cart also covers
            // checkout, and Account covers sign-in.
            aria-current={active ? 'page' : undefined}
            aria-label={badge > 0 ? `${label}, ${badge} item${badge === 1 ? '' : 's'}` : undefined}
            className="group flex min-w-0 flex-1 flex-col items-center justify-center gap-1"
          >
            <span
              className={cn(
                'relative grid h-8 w-14 place-items-center rounded-full transition-colors duration-200',
                active
                  ? 'bg-brand-50 text-brand-500'
                  : 'text-content-muted group-active:bg-surface-muted',
              )}
            >
              <Icon className="h-[21px] w-[21px]" aria-hidden="true" />
              {badge > 0 && (
                <span className="absolute -top-1 right-1.5 min-w-[18px] rounded-full bg-accent-500 px-1 text-center text-[10px] font-bold leading-[18px] text-white ring-2 ring-surface">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </span>
            <span
              className={cn(
                'max-w-full truncate px-1 text-[11px]',
                active ? 'font-bold text-brand-500' : 'font-semibold text-content-muted',
              )}
            >
              {label}
            </span>
          </Link>
        ))}
      </div>
    </nav>
  );
}
